<script setup lang="ts">
import { computed } from 'vue';
import { drawerProps } from 'naive-ui';
import GaiaText from './GaiaText.vue';

const props = defineProps({
  ...drawerProps,
  /** Gaia drawers slide in from the right (the view's column editor). */
  placement: {
    type: String as () => 'top' | 'right' | 'bottom' | 'left',
    default: 'right',
  },
  width: {
    type: [Number, String],
    default: 640,
  },
  title: {
    type: String,
    default: undefined,
  },
  /** Muted line under the title. */
  subtitle: {
    type: String,
    default: undefined,
  },
  /**
   * Hand the body's height to the content instead of scrolling it here: the
   * drawer's own padding goes, and the slot gets a box with a definite height
   * to fill (the file preview and the notes editor both size themselves at
   * 100% and manage their own scroll). naive's styled scrollbar auto-sizes its
   * content, so `height: 100%` inside it collapses to nothing — hence the swap
   * to the native scroller, which is what gives the wrapper its height.
   */
  flush: {
    type: Boolean,
    default: false,
  },
});

// `title`/`subtitle`/`flush` are ours — n-drawer has no header of its own, the
// content does, so they must not land on the DOM element.
const passThrough = computed(() => {
  const { title, subtitle, flush, ...rest } = props;
  return rest;
});
</script>

<template>
  <!-- Right-hand editing drawer (the CM view's column editor: settings,
       question, filters). n-drawer-content brings the header/footer chrome and
       the close button; the header slot here is title + subtitle, which is how
       every Gaia drawer opens. -->
  <n-drawer v-bind="passThrough">
    <n-drawer-content
      closable
      :native-scrollbar="props.flush"
      :body-content-style="props.flush ? { height: '100%', padding: '0' } : undefined"
    >
      <template v-if="props.title || $slots.header" #header>
        <slot name="header">
          <gaia-text tag="div" size="subtitle" weight="semibold" tone="ink">
            {{ props.title }}
          </gaia-text>
          <gaia-text
            v-if="props.subtitle"
            tag="div"
            size="control"
            tone="subtle"
            :style="{ marginTop: '3px' }"
          >
            {{ props.subtitle }}
          </gaia-text>
        </slot>
      </template>
      <slot />
      <template v-if="$slots.footer" #footer>
        <slot name="footer" />
      </template>
    </n-drawer-content>
  </n-drawer>
</template>
